import { useCallback, useEffect, useRef, useState } from "react";

export type SecondaryTab = "quotes" | "wallet" | "portfolio";

const STORAGE_KEY = "secondaryPanel:v1";
const DEBOUNCE_MS = 200;

export interface SecondaryPanelPreference {
    isOpen: boolean;
    tab: SecondaryTab;
    width: string;
}

const DEFAULT_PREFERENCE: SecondaryPanelPreference = {
    isOpen: false,
    tab: "quotes",
    width: "48vw",
};

function readPreference(): SecondaryPanelPreference {
    if (typeof window === "undefined") return DEFAULT_PREFERENCE;
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return DEFAULT_PREFERENCE;
        const parsed = JSON.parse(raw) as Partial<SecondaryPanelPreference>;
        const tab = parsed.tab;
        const validTab = tab === "quotes" || tab === "wallet" || tab === "portfolio" ? tab : DEFAULT_PREFERENCE.tab;
        return {
            isOpen: Boolean(parsed.isOpen),
            tab: validTab,
            width: typeof parsed.width === "string" && parsed.width.length > 0 ? parsed.width : DEFAULT_PREFERENCE.width,
        };
    } catch {
        return DEFAULT_PREFERENCE;
    }
}

export function useSecondaryPanelPreference() {
    const [preference, setPreference] = useState<SecondaryPanelPreference>(DEFAULT_PREFERENCE);
    const hydratedRef = useRef(false);
    const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        setPreference(readPreference());
        hydratedRef.current = true;
    }, []);

    const persist = useCallback((next: SecondaryPanelPreference) => {
        if (!hydratedRef.current) return;
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        saveTimerRef.current = setTimeout(() => {
            try {
                localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
            } catch {
                /* ignore quota errors */
            }
        }, DEBOUNCE_MS);
    }, []);

    const setIsOpen = useCallback((value: boolean | ((prev: boolean) => boolean)) => {
        setPreference((prev) => {
            const isOpen = typeof value === "function" ? value(prev.isOpen) : value;
            const next = { ...prev, isOpen };
            persist(next);
            return next;
        });
    }, [persist]);

    const setTab = useCallback((tab: SecondaryTab) => {
        setPreference((prev) => {
            const next = { ...prev, tab };
            persist(next);
            return next;
        });
    }, [persist]);

    const toggleOpen = useCallback(() => {
        setIsOpen((prev) => !prev);
    }, [setIsOpen]);

    return {
        isOpen: preference.isOpen,
        tab: preference.tab,
        width: preference.width,
        setIsOpen,
        setTab,
        toggleOpen,
    };
}
